import {
  AfterViewInit, ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output, signal,
  ViewChild
} from '@angular/core';
import { ConfigAwareService } from "../../config/models/config";
import { LookerService } from "../../services/looker.service";
import { DashboardEvent, DrillMenuEvent, ILookerConnection } from "@looker/embed-sdk";
import { ReportingActions } from "../../../store/actions/reporting.actions";
import { DAGSTER_MODELS } from "../../app.constants";
import { Store } from "@ngrx/store";
import { Helpers } from "../../shared/helpers";
import { Router } from "@angular/router";
import { selectDefaultFilters } from "../../../store/selectors/reporting.selectors";
import { filter, take } from "rxjs/operators";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { BehaviorSubject, combineLatest } from "rxjs";
import { Theme } from "../../services/theme.service";
import { UrlParams } from "@looker/embed-sdk/src/types";
import { SvgImagesComponent } from "../svg-images/svg-images.component";
import { NgClass } from "@angular/common";
import { trackDashboard } from "../../../store";

@Component({
  selector: 'app-looker-embed-sdk',
  imports: [
    SvgImagesComponent,
    NgClass
  ],
  templateUrl: './looker-embed-sdk.component.html',
  styleUrl: './looker-embed-sdk.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LookerEmbedSdkComponent extends ConfigAwareService implements AfterViewInit {
  private lookerService = inject(LookerService)
  private store = inject(Store)
  private router = inject(Router)
  private destroyRef = inject(DestroyRef)

  filters$ = new BehaviorSubject(null);
  hiddenFilters$ = new BehaviorSubject([]);
  dashboard$ = new BehaviorSubject(null);

  @Input() type: 'dashboard' | 'custom' = 'dashboard';
  @Input() resetFilters: string[] = [];

  @Input() set filters(value: object) {
    if (!value) return;
    const filters = Helpers.removeEmpty(value);
    this.filters$.next(filters);
    if (this.connection) {
      this.update(filters);
    }
  }

  @Input() set hiddenFilters(value: string[]) {
    if (!value) return;
    this.hiddenFilters$.next(value);
  }

  @Input() set theme(value: Theme) {
    if (!value) return;
    if (this.connection) {
      this.initialize()
    }
  }

  @Input() set dashboard(value: string) {
    if (!value) return;
    this.dashboard$.next(value);
    this.defaultFiltersSet.set(false);
    if (this.connection) {
      this.loadWithFilters({ dashboard: value, filters: this.filters$.value });
    }
  }

  @Input() hideAllFilters = false;
  @Input() dense = false;

  @Output() onDrillMenuClick = new EventEmitter<DrillMenuEvent>()
  @Output() hasScenarioFilter = new EventEmitter<boolean>()
  @Output() onLoaded = new EventEmitter<DashboardEvent>()

  @ViewChild('looker', { static: true }) dashboardElement: ElementRef<HTMLElement>

  loading = signal(true);
  defaultFiltersSet = signal(false);
  connection: ILookerConnection;

  ngAfterViewInit() {
    combineLatest([this.filters$, this.dashboard$]).pipe(
      filter(([filters, dashboard]) => !!filters && !!dashboard),
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(async () => {
      await this.initialize();
    });
  }

  private dashboardLoaded(data: DashboardEvent) {
    const { dashboard_filters } = data.dashboard;
    const isFiltersReset = this.resetFilters.some(key => dashboard_filters[key] === '')
    if (!isFiltersReset && !this.defaultFiltersSet()) {
      this.store.dispatch(ReportingActions.setDefaultFilters({
        filters: dashboard_filters,
        report: this.dashboard$.value
      }));
      this.defaultFiltersSet.set(true);
    }
    this.onLoaded.emit(data);
    this.loading.set(false);
    if (data.status === 'complete') {
      this.hasScenarioFilter.emit(dashboard_filters.hasOwnProperty('Scenario'));
    }

    // Track dashboard load
    const { dashboard, tile, type } = data;

    this.store.dispatch(trackDashboard({
      label: 'Looker Dashboard ' + type,
      data: {
        title: (tile?.title || dashboard?.title),
        ...dashboard_filters
      }
    }));
  }

  private syncFilters(data: DashboardEvent) {
    const { dashboard_filters } = data.dashboard;
    const isFiltersReset = this.resetFilters.some(key => dashboard_filters[key] === '');
    if (!isFiltersReset) {
      this.syncFiltersToUrl(dashboard_filters);
    } else {
      this.loading.set(true);
      this.restoreDefaultFilters()
    }
  }

  private syncFiltersToUrl(filters: UrlParams, sync = true) {
    const params = this.router.routerState.snapshot.root.queryParams;
    const newParams = Helpers.removeEmpty({ ...params, ...filters });
    if (sync) {
      this.store.dispatch(ReportingActions.updateFilters({ filters: newParams }));
    }
    this.router.navigate([], { queryParams: newParams });
  }

  private restoreDefaultFilters() {
    this.store.select(selectDefaultFilters(this.dashboard$.value)).pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((filters) => {
      this.update(filters);
    })
  }

  public update(filters: any) {
    this.connection.asDashboardConnection().updateFilters(filters);
    this.connection.asDashboardConnection().run();
  }

  private async initialize() {
    this.dashboardElement.nativeElement.innerHTML = ''; // Always reset
    this.connection = null;
    this.loading.set(true);

    const sdk = await this.lookerService.getSdk();
    const embed = this.type === 'custom'
      ? sdk.createDashboardWithId(this.dashboard$.value)
      : sdk.createDashboardWithUrl(`/dashboards/${ this.model }::${ this.dashboard$.value }`);

    embed
      .withFilters(this.filters$.value)
      .withParams({
        theme: this.lookerService.theme,
        hide_filter: this.hiddenFilters$.value,
        ...(this.dense && {
          _theme: JSON.stringify({
            show_title: false,
            show_last_updated_indicator: false,
            show_dashboard_menu: false,
            show_explore_header: false,
          })
        }),
        ...(this.hideAllFilters && {
          _theme: JSON.stringify({
            show_dashboard_header: false,
            show_filters_toggle: false,
            show_filters_bar: false,
            show_title: false
          })
        })
      })
      .appendTo(this.dashboardElement.nativeElement)
      .on('drillmenu:click', (value) => {
        this.onDrillMenuClick.emit(value);
        return { cancel: true }
      })
      .on('dashboard:loaded', this.dashboardLoaded.bind(this))
      .on('dashboard:run:complete', this.dashboardLoaded.bind(this))
      .on('dashboard:filters:changed', this.syncFilters.bind(this))
      .on('dashboard:tile:explore', () => {
        this.hasScenarioFilter.emit(false);
        return { cancel: false };
      })
      .build()
      .connect()
      .then((connection: ILookerConnection) => {
        this.connection = connection;
      })
      .catch((err: any) => {
        console.error('Looker embed error:', err);
        this.loading.set(false);
      });
  }

  get model() {
    return DAGSTER_MODELS[this.appConfig.ramp.env];
  }

  async loadWithFilters({ dashboard, filters }: { dashboard: string, filters: object }) {
    filters = Helpers.removeEmpty(filters);
    filters['theme'] = this.lookerService.theme;
    filters['hide_filter'] = this.hiddenFilters$.value;
    filters['_theme'] = JSON.stringify({
      show_dashboard_header: !this.hideAllFilters,
      show_filters_toggle: !this.hideAllFilters,
      show_filters_bar: !this.hideAllFilters,
      show_title: !this.hideAllFilters
    });

    if (this.type === 'custom') {
      dashboard = `/dashboards/${ dashboard }`;
    } else {
      dashboard = `/dashboards/${ this.model }::${ dashboard }`;
    }

    const url = `${ dashboard }?${ Helpers.objectToUrlParams({
      ...this.filters$.value,
      ...filters,
    }) }`;

    await this.connection.loadUrl({
      url
    })
  }
}
